import React from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { useAuth } from "@/App";
import { CheckCircle2, Sparkles, Lock } from "lucide-react";

/**
 * PanelCostBadge — compact badge for AI panel headers.
 *
 * iter460 — Cost-per-action is now SHOWN per user request:
 *   "трябва да се обновят модулите, където има ai анализ - и да се постави
 *    етикет колко ai анализа взема"
 *
 * Behaviour:
 *   • Paid/admin users on AI features  → "Включено · 1 AI анализ"
 *   • Free users on AI features        → "Надгр. за достъп · 1 AI анализ"
 *   • Non-AI panels                    → "Включено" (no cost shown)
 */
export default function PanelCostBadge({ credits = 0, featureId = null, note = null, compact = false }) {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const { user } = useAuth();
  const bg = language === "bg";

  const usesAI = (credits && credits > 0) || !!featureId;
  const tier = user?.subscription?.status || "none";
  const isPaid = tier === "active" || tier === "trialing" || user?.role === "admin";

  // Numeric cost suffix shown for AI features when we know the credit cost.
  const costSuffix = credits && credits > 0
    ? (bg
        ? (credits === 1 ? " · едно разчитане" : ` · ${credits} разчитания`)
        : (credits === 1 ? " · 1 AI credit" : ` · ${credits} AI credits`))
    : "";

  // Non-AI panels → "Included" green badge
  if (!usesAI) {
    return (
      <span
        className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
          isDark ? "bg-emerald-900/30 text-emerald-300" : "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
        }`}
        data-testid="panel-cost-badge"
      >
        <CheckCircle2 className="w-2.5 h-2.5" />
        {bg ? "Включено" : "Included"}
      </span>
    );
  }

  // Paid / trial / admin → "Included in your plan · N AI credits"
  if (isPaid) {
    return (
      <span
        className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
          isDark ? "bg-violet-900/30 text-violet-300" : "bg-violet-50 text-violet-700 ring-1 ring-violet-200"
        }`}
        data-testid="panel-cost-badge"
      >
        <Sparkles className="w-2.5 h-2.5" />
        {bg ? "Включено в плана" : "Included in plan"}{costSuffix}
        {!compact && note && <span className="font-normal opacity-75 ml-0.5">{note}</span>}
      </span>
    );
  }

  // Free / expired → "Upgrade · N AI credits"
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
        isDark ? "bg-amber-900/30 text-amber-300" : "bg-amber-50 text-amber-700 ring-1 ring-amber-200"
      }`}
      data-testid="panel-cost-badge"
    >
      <Lock className="w-2.5 h-2.5" />
      {bg ? "Нужен е по-висок план" : "Upgrade to access"}{costSuffix}
    </span>
  );
}
